/* eslint-disable max-lines-per-function */
import { ChartDatasetModel } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { Component, booleanAttribute, computed, input } from '@angular/core';
import Highcharts, { Options, Point } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';

@Component({
  selector: 'procure-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  imports: [HighchartsChartComponent],
})
export class PieChart {
  readonly dataset = input.required<ChartDatasetModel | undefined | null>();
  readonly legendPosition = input<'right' | 'top' | 'bottom'>('bottom');
  readonly height = input<number>(250);

  readonly half = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly chartOptions = computed<Options>(() => {
    const highcharts = Highcharts;
    const legendPosition = this.legendPosition();
    const legendVerticalAlign = this.getLegendVerticalAlign(legendPosition);

    return {
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      chart: {
        type: 'pie',
        height: this.height(),
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
          startAngle: this.half() ? -90 : 0,
          endAngle: this.half() ? 90 : 0,
          size: this.half() ? '150%' : '100%',
          center: ['50%', this.half() ? '90%' : '50%'],
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormatter: function (this: Point): string {
          const amount = typeof this.y === 'number' ? this.y : 0;
          const formattedAmount = highcharts.numberFormat(amount, 0);
          return `${this.name ?? ''}: <b>${formattedAmount}</b>`;
        },
      },
      legend: {
        alignColumns: false,
        verticalAlign: legendVerticalAlign,
        align: legendPosition === 'right' ? 'right' : 'center',
        layout: legendPosition === 'right' ? 'vertical' : 'horizontal',
        squareSymbol: true,
        symbolRadius: 0,
        labelFormatter: function (this: Point): string {
          const amount = typeof this.y === 'number' ? this.y : 0;
          const formattedAmount = highcharts.numberFormat(amount, 0);

          return `${this.name ?? ''}: <b>${formattedAmount}</b>`;
        },
        useHTML: true,
      },
      series: [
        {
          type: 'pie',
          name: this.dataset()?.label ?? '',
          data: this.dataset()?.data?.map((data) => ({
            name: data.name ?? '',
            y: data.value || 0,
          })),
          innerSize: '50%',
        },
      ],
    };
  });

  private getLegendVerticalAlign(
    position: 'right' | 'top' | 'bottom',
  ): 'top' | 'bottom' | 'middle' {
    switch (position) {
      case 'top':
        return 'top';
      case 'bottom':
        return 'bottom';
      default:
        return 'middle';
    }
  }
}
